import { useCallback, useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

export interface OtpInputProps {
  /** Current OTP value, digits only (0–`length` characters). */
  value: string;
  /** Called when the value changes; always digit-only. */
  onChange: (value: string) => void;
  /** Number of digits to collect. Defaults to 6. */
  length?: number;
  /** Disables all slots. */
  disabled?: boolean;
  /** Invoked once the user has filled all slots. */
  onComplete?: (value: string) => void;
  /** Auto-focus the first empty slot on mount. */
  autoFocus?: boolean;
  /** Accessible label applied to each slot. */
  ariaLabel?: string;
  /** Additional class names on the root container. */
  className?: string;

  fieldClassName?: string;
}

/**
 * Segmented one-time-password input.
 *
 * Renders a row of single-character slots with keyboard navigation,
 * backspace-to-previous, clipboard paste, and auto-advance focus.
 *
 * @example
 * ```tsx
 * const [code, setCode] = useState('');
 * <OtpInput value={code} onChange={setCode} onComplete={submit} />
 * ```
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  onComplete,
  autoFocus,
  ariaLabel = 'Verification code digit',
  className,
  fieldClassName,
}: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const focusSlot = useCallback((index: number) => {
    const el = inputsRef.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  useEffect(() => {
    if (autoFocus) {
      const firstEmpty = Math.min(value.length, length - 1);
      focusSlot(firstEmpty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setDigits(next: string) {
    const sanitized = next.replace(/\D/g, '').slice(0, length);
    onChange(sanitized);
    if (sanitized.length === length) onComplete?.(sanitized);
  }

  function handleChange(index: number, raw: string) {
    const onlyDigits = raw.replace(/\D/g, '');
    if (!onlyDigits) return;

    const chars = value.split('');
    // Accept last typed digit so overwriting a filled slot works.
    chars[index] = onlyDigits[onlyDigits.length - 1];
    const joined = chars.join('').slice(0, length);
    setDigits(joined);

    const nextIndex = Math.min(index + 1, length - 1);
    if (nextIndex !== index) focusSlot(nextIndex);
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const chars = value.split('');
      if (chars[index]) {
        chars[index] = '';
        setDigits(chars.join(''));
        return;
      }
      if (index > 0) {
        chars[index - 1] = '';
        setDigits(chars.join(''));
        focusSlot(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusSlot(index - 1);
      return;
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusSlot(index + 1);
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text');
    const onlyDigits = pasted.replace(/\D/g, '');
    if (!onlyDigits) return;
    e.preventDefault();
    const truncated = onlyDigits.slice(0, length);
    setDigits(truncated);
    focusSlot(Math.min(truncated.length, length - 1));
  }

  return (
    <div className={cn('flex items-center justify-between gap-2', className)} role="group" aria-label="One-time password">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`${ariaLabel} ${index + 1}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          className={cn(
            'size-12 text-center text-xl font-semibold tabular-nums',
            'bg-surface text-primary',
            'border border-border rounded-card',
            'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/40',
            'disabled:opacity-50 disabled:pointer-events-none',
            'transition-colors',
            digit && 'border-accent',
            fieldClassName,
          )}
        />
      ))}
    </div>
  );
}
